import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MailX, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'form'
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleUnsubscribeWithToken(token);
    } else {
      setStatus('form');
    }
  }, [searchParams]);

  const handleUnsubscribeWithToken = async (token) => {
    try {
      await axios.post(`${API}/api/newsletter/unsubscribe`, null, {
        params: { token }
      });
      setStatus('success');
    } catch (error) {
      setStatus('error');
    }
  };

  const handleUnsubscribeWithEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/newsletter/unsubscribe`, null, {
        params: { email: email.trim() }
      });
      setStatus('success');
    } catch (error) {
      if (error.response?.status === 404) {
        setStatus('not_found');
      } else {
        setStatus('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-slate-600">Processing your request...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Unsubscribed Successfully</h1>
            <p className="text-slate-600 mb-6">
              You've been removed from our newsletter. We're sorry to see you go!
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h1>
            <p className="text-slate-600 mb-6">
              We couldn't process your unsubscribe request. Please try again or contact support.
            </p>
            <button
              onClick={() => setStatus('form')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-full font-semibold"
            >
              Try Again
            </button>
          </div>
        )}

        {status === 'not_found' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Email Not Found</h1>
            <p className="text-slate-600 mb-6">
              This email address isn't in our subscriber list. You may have already unsubscribed.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        )}

        {status === 'form' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MailX className="w-8 h-8 text-slate-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">Unsubscribe</h1>
            <p className="text-slate-600 mb-6 text-center">
              Enter your email address to unsubscribe from our newsletter.
            </p>
            
            <form onSubmit={handleUnsubscribeWithEmail} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                data-testid="unsubscribe-email-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                data-testid="unsubscribe-button"
              >
                {submitting ? 'Processing...' : 'Unsubscribe'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <Link
                to="/blog"
                className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm"
              >
                Cancel and go back
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
